import { Link } from 'react-router-dom';
import OfficialAvatar from '../OfficialAvatar';
import QuietStateCard from './QuietStateCard';

interface Adoption {
  id: string;
  behavingStatus?: string;
  changedSinceLastVisit?: boolean;
  official: {
    id: string;
    name: string;
    photoUrl: string;
    officeName: string;
    state: string;
    accountabilityScore: number | null;
  };
}

interface WhatChangedRailProps {
  changedAdoptions: Adoption[];
  newNotificationsCount: number;
  totalWatched: number;
  behavingBadgeClass: (status?: string) => string;
  behavingLabel: (status?: string) => string;
}

export default function WhatChangedRail({
  changedAdoptions,
  newNotificationsCount,
  totalWatched,
  behavingBadgeClass,
  behavingLabel,
}: WhatChangedRailProps) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-1">
            Primary Rail
          </p>
          <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
            What Changed Since Your Last Visit
          </h2>
        </div>
        <Link
          to="/notifications"
          className="text-xs font-black uppercase tracking-widest text-gold-600 hover:underline dark:text-gold-400"
        >
          View Alerts
        </Link>
      </div>

      {changedAdoptions.length === 0 ? (
        <QuietStateCard
          eyebrow="Quiet Period"
          title="No watched officials moved"
          description={`Your ${totalWatched === 0 ? 'watchlist is still empty' : 'watchlist is stable'} right now. ${newNotificationsCount > 0 ? `You still have ${newNotificationsCount} fresh alert${newNotificationsCount === 1 ? '' : 's'} to review.` : 'When score movement or fresh evidence lands, it will show up here first.'}`}
          ctaLabel={totalWatched === 0 ? 'Browse Officials' : 'Review Notifications'}
          ctaTo={totalWatched === 0 ? '/officials' : '/notifications'}
        />
      ) : (
        <div className="space-y-3">
          {changedAdoptions.map((adoption) => (
            <div key={adoption.id} className="glass-panel rounded-lg border-l-4 border-l-gold-500 p-4 transition hover:border-gold-400">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Link to={`/officials/${adoption.official.id}`} className="flex min-w-0 items-center gap-4">
                  <OfficialAvatar
                    name={adoption.official.name}
                    photoUrl={adoption.official.photoUrl}
                    alt={adoption.official.name}
                    className="h-12 w-12 shrink-0 rounded-full bg-navy-100 dark:bg-navy-900 flex items-center justify-center"
                    imgClassName="h-12 w-12 rounded-full object-cover"
                    initialsClassName="text-sm font-black uppercase text-navy-500 dark:text-cream-300"
                  />
                  <div className="min-w-0">
                    <p className="truncate font-bold text-navy-900 transition hover:text-gold-600 dark:text-cream-50 dark:hover:text-gold-400">
                      {adoption.official.name}
                    </p>
                    <p className="truncate text-xs text-navy-400 dark:text-navy-300">
                      {adoption.official.officeName}
                      {adoption.official.state ? ` · ${adoption.official.state}` : ''}
                    </p>
                    <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                      Fresh movement detected
                    </p>
                  </div>
                </Link>

                <div className="flex items-center gap-3 sm:justify-end">
                  {adoption.behavingStatus && (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white ${behavingBadgeClass(adoption.behavingStatus)}`}
                      title={`Status: ${behavingLabel(adoption.behavingStatus)}`}
                    >
                      {behavingLabel(adoption.behavingStatus)}
                    </span>
                  )}
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                      Spine
                    </p>
                    <p className="text-lg font-black text-navy-900 dark:text-cream-50">
                      {adoption.official.accountabilityScore ?? '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
